import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
// ActionCable's own monitor waits out a 6s stale threshold and then backs off
// up to 30s before it reopens a dropped socket, so the dashboard sat on the
// yellow "Reconnecting" banner the whole time (reported 2026-10-14). Reopen
// from here instead, but only when the socket is really closed: in
// @rails/actioncable 6.1.3 reopen() closes a socket that is open or still
// connecting and reopens it 500ms later, so calling it on a slow handshake
// would kill the connection on every attempt. A socket that is still closing
// is not active either, and reopen() replaces it immediately, so the next
// retry must wait out the close instead of aborting the new handshake.
// Attempts back off up to 30s.
const REOPEN_INTERVAL = 3000;
const MAX_REOPEN_INTERVAL = 30000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.reopenTimer = null;
    this.reopenAttempts = 0;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.clearReopenTimer();
      this.reopenAttempts = 0;
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.reopenConnection();
      this.initReconnectTimer();
    }
  }

  // Ask ActionCable to reopen the socket, only when it is closed. A socket that
  // is open, connecting, or still closing is left alone: reopen() kills an
  // open or connecting socket, and it replaces one that is still closing.
  reopenConnection() {
    if (this.reopenTimer) return;
    if (this.isSocketInProgress()) return;
    try {
      this.consumer.connection.reopen();
    } catch (error) {
      // The socket can already be gone. The next attempt handles it.
    }
    const delay = Math.min(
      REOPEN_INTERVAL * 2 ** this.reopenAttempts,
      MAX_REOPEN_INTERVAL
    );
    this.reopenAttempts += 1;
    this.reopenTimer = setTimeout(() => {
      this.reopenTimer = null;
    }, delay);
  }

  // isActive() is only open or connecting. A socket that is still closing is
  // neither, but reopen() would throw it away and start a new one immediately.
  isSocketInProgress() {
    const { connection } = this.consumer;
    if (connection.isActive()) return true;
    const state =
      typeof connection.getState === 'function' ? connection.getState() : null;
    return state === 'closing';
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  clearReopenTimer = () => {
    if (this.reopenTimer) {
      clearTimeout(this.reopenTimer);
      this.reopenTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.clearReconnectTimer();
    this.clearReopenTimer();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
